import React, { useState } from 'react'
import { Plus } from 'lucide-react'
import { GoalService, type Goal, type UpdateGoalRequest } from '../../../services/goalService'
import { InlinePopover, ChipRow } from '../../tasks/inline/InlinePopover'
import { ParentPicker, type ParentType } from '../../hierarchy/ParentPicker'
import { GOAL_PRIORITY_OPTIONS } from '../../../utils/goalOptions'
import { priorityWord } from '../../../utils/goalWords'
import { Timestamp } from '../../ui/Timestamp'

/**
 * A goal's facts, each edited where it is shown (Goals UX pass 2026-09-06,
 * the task page's philosophy): the title, why it matters, what done means,
 * by when, what it belongs to, priority. With these the edit page has nothing
 * left to offer and leaves.
 */
interface EditorProps {
  goal: Goal
  onChanged: (goal: Goal) => void
  trigger?: React.ReactNode
  variant?: 'dotted' | 'chip' | 'icon'
  className?: string
  openRequest?: number
}

async function write(goal: Goal, payload: UpdateGoalRequest, onChanged: (g: Goal) => void) {
  const svc = new GoalService()
  await svc.updateGoal(goal.id, payload)
  onChanged(await svc.getGoal(goal.id))
}

const inputClass = 'w-full rounded-md border border-soft bg-surface px-2 py-1.5 text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand/30'

// ── Progress: the percentage in Keeping up is the trigger ─────────────────
// The goal page carried a "Progress" panel below the journey ("Start logging
// your progress to track your journey and celebrate milestones! 🎯") under a
// Keeping up block that already drew the bar — the same number three times
// (North Star rules 2 and 7, 2026-09-12). Now the bar's percentage opens this.
const PROGRESS_CHIPS = [0, 25, 50, 75, 100].map((n) => ({ value: String(n), label: n === 100 ? 'Done' : `${n}%` }))

export function GoalProgressEditor({ goal, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(String(Math.round(Number(goal.progress || 0))))
  const save = async () => {
    const n = Math.round(Number(value))
    if (!Number.isFinite(n) || n < 0 || n > 100) throw new Error('A number from 0 to 100.')
    await write(goal, n >= 100 ? { progress: 100, status: 'completed' } : { progress: n }, onChanged)
  }
  return (
    <InlinePopover label="How far along" variant={variant} className={className} onOpen={reset} onSave={save} testId="goal-edit-progress" width="w-80"
                   trigger={trigger ?? `${Math.round(Number(goal.progress || 0))}%`}>
      <ChipRow label="How far along" options={PROGRESS_CHIPS} value={PROGRESS_CHIPS.some((c) => c.value === value) ? value : ''} onChange={setValue} />
      <input type="number" min={0} max={100} step={5} value={value} onChange={(e) => setValue(e.target.value)} aria-label="Percent" className={`${inputClass} w-28`} />
    </InlinePopover>
  )
}

export function GoalTitleEditor({ goal, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(goal.title || '')
  const save = async () => {
    const text = value.trim()
    if (!text) throw new Error('A goal needs a name.')
    if (text === (goal.title || '').trim()) return
    await write(goal, { title: text }, onChanged)
  }
  return (
    <InlinePopover label="Title" variant={variant} className={className} onOpen={reset} onSave={save} testId="goal-edit-title" width="w-96" trigger={trigger ?? goal.title}>
      <input type="text" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Title" autoFocus className={inputClass} placeholder="Name the goal as the outcome — Run a 10k, A senior role, Debt-free" />
    </InlinePopover>
  )
}

function TextFactEditor({ goal, onChanged, trigger, variant = 'dotted', className, field, label, placeholder, current, rows = 2 }: EditorProps & {
  field: 'description' | 'success_criteria'
  label: string
  placeholder: string
  current: string | null | undefined
  rows?: number
}) {
  const [text, setText] = useState('')
  const reset = () => setText(current || '')
  const save = async () => { await write(goal, { [field]: text.trim() || null } as UpdateGoalRequest, onChanged) }
  return (
    <InlinePopover label={label} variant={variant} className={className} onOpen={reset} onSave={save} testId={`goal-edit-${field}`} width="w-96"
                   trigger={trigger ?? (current || <><Plus className="h-3 w-3" /> {label.toLowerCase()}</>)}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={rows} aria-label={label} autoFocus placeholder={placeholder} className={`${inputClass} resize-y`} />
      {current && <button type="button" onClick={() => setText('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">Clear it</button>}
    </InlinePopover>
  )
}

export function GoalWhyEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="description" label="Why it matters" current={p.goal.description} placeholder="What reaching this would change — one line is plenty" />
}

export function GoalDoneMeansEditor(p: EditorProps) {
  return <TextFactEditor {...p} field="success_criteria" label="Done means" current={p.goal.success_criteria} placeholder="What will be true when this is done — the old jeans fit, the offer letter is signed" />
}

export function GoalTargetDateEditor({ goal, onChanged, trigger, variant = 'dotted', className, openRequest }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue((goal.target_date || '').slice(0, 10))
  const save = async () => { await write(goal, { target_date: value || null }, onChanged) }
  return (
    <InlinePopover label="Get there by" variant={variant} className={className} onOpen={reset} onSave={save} testId="goal-edit-target-date" openRequest={openRequest}
                   trigger={trigger ?? (goal.target_date ? <>by <Timestamp value={`${goal.target_date.slice(0, 10)}T12:00:00`} mode="date" /></> : <><Plus className="h-3 w-3" /> by when</>)}>
      <input type="date" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Get there by" className={inputClass} />
      <p className="text-app-micro text-muted">A rough date is fine. When it passes, I will ask how it is going — nothing more.</p>
      {goal.target_date && <button type="button" onClick={() => setValue('')} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">No date</button>}
    </InlinePopover>
  )
}

export function GoalPriorityEditor({ goal, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(goal.priority || 'medium')
  const save = async () => { await write(goal, { priority: (value || 'medium') as UpdateGoalRequest['priority'] }, onChanged) }
  return (
    <InlinePopover label="Priority" variant={variant} className={className} onOpen={reset} onSave={save} testId="goal-edit-priority" trigger={trigger ?? priorityWord(goal.priority)}>
      <ChipRow label="Priority" options={GOAL_PRIORITY_OPTIONS} value={value} onChange={setValue} />
    </InlinePopover>
  )
}

export function GoalParentEditor({ goal, onChanged, trigger, variant = 'icon', className }: EditorProps) {
  const [parentType, setParentType] = useState<ParentType | ''>('')
  const [parentId, setParentId] = useState<number | string | null>(null)
  const reset = () => { setParentType(goal.parent_dream_id ? 'dream' : ''); setParentId(goal.parent_dream_id ?? null) }
  const save = async () => { await write(goal, { parent_dream_id: parentType === 'dream' && parentId ? Number(parentId) : null }, onChanged) }
  return (
    <InlinePopover label="Belongs to" variant={variant} className={className} onOpen={reset} onSave={save} testId="goal-edit-parent" width="w-96"
                   trigger={trigger ?? (goal.parent?.title || <><Plus className="h-3 w-3" /> belongs to a dream</>)}>
      <ParentPicker allowedTypes={['dream']} parentType={parentType} parentId={parentId} onChange={(f) => { setParentType((f.parent_type || '') as ParentType | ''); setParentId(f.parent_id || null) }} />
    </InlinePopover>
  )
}

/** The "Add" row: the facts not yet set, related ones together. */
export function GoalAddRow({ goal, onChanged }: { goal: Goal; onChanged: (goal: Goal) => void }) {
  const plan: React.ReactNode[] = []
  if (!goal.target_date) plan.push(<GoalTargetDateEditor key="by" goal={goal} onChanged={onChanged} variant="chip" />)
  const carry: React.ReactNode[] = []
  if (!goal.description) carry.push(<GoalWhyEditor key="why" goal={goal} onChanged={onChanged} variant="chip" />)
  if (!goal.success_criteria) carry.push(<GoalDoneMeansEditor key="done" goal={goal} onChanged={onChanged} variant="chip" />)
  const belong: React.ReactNode[] = []
  if (!goal.parent?.title && !goal.parent_dream_id) belong.push(<GoalParentEditor key="parent" goal={goal} onChanged={onChanged} variant="chip" />)
  if (goal.priority !== 'high' && goal.priority !== 'critical') belong.push(<GoalPriorityEditor key="priority" goal={goal} onChanged={onChanged} variant="chip" trigger={<>priority · {priorityWord(goal.priority)}</>} />)
  const groups = [plan, carry, belong].filter((g) => g.length > 0)
  if (groups.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="goal-add-details">
      <span className="text-app-micro text-muted">Add</span>
      {groups.map((g, i) => <span key={i} className="flex flex-wrap items-center gap-1.5" data-testid="goal-add-group">{g}</span>)}
    </div>
  )
}
